/**
 * Types métier mappés sur les tables `public.aurora_prospection_*`.
 * Source de vérité : `src/backend/api/migrations/0001_init_prospection.sql`.
 */

export type ProspectStatus =
  | "raw"
  | "verified"
  | "qualified"
  | "awaiting_review"
  | "ready"
  | "edited"
  | "rejected"
  | "sent"
  | "replied"
  | "bounced"
  | "unsubscribed";

export type ProspectSource = "apify_leads_finder" | "apify_linkedin" | "apify_maps" | "manuel";

export type EmailStatus = "ok" | "catch_all" | "unknown" | "invalid" | "disposable" | "role";

export type ReplyIntent = "interested" | "not_interested" | "ooo" | "unsubscribe" | "negative";

export interface PersonalizationVariant {
  subject: string;
  body: string;
  angles_emphasis: string[];
}

export interface PersonalizationVariants {
  variants: PersonalizationVariant[];
}

export interface Prospect {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  mobile_number: string | null;
  personal_email: string | null;

  seniority_level: string | null;
  functional_level: string | null;
  city: string | null;
  country: string | null;

  company_name: string | null;
  company_website: string | null;
  company_size: string | null;
  industry: string | null;
  company_description: string | null;
  company_annual_revenue: string | null;
  company_founded_year: string | null;
  company_technologies: string | null;
  company_linkedin_url: string | null;
  company_phone: string | null;

  email_status: EmailStatus | null;
  email_quality_score: number | null;
  email_verified_at: string | null;

  website_summary: string | null;
  company_summary: string | null;
  contact_summary: string | null;
  ai_needs_summary: string | null;
  angles_pitched: string[] | null;
  batch_label: string | null;
  segment: string | null;
  enriched_at: string | null;

  status: ProspectStatus;
  source: ProspectSource;
  owner_email: string | null;

  personalization_subject: string | null;
  personalization_body: string | null;
  personalization_variants: PersonalizationVariants | null;
  personalization_model: string | null;
  personalization_chosen_variant_idx: number | null;
  personalized_at: string | null;
  review_notes: string | null;

  smartlead_campaign_id: string | null;
  smartlead_lead_id: string | null;
  sent_at: string | null;

  reply_at: string | null;
  reply_intent: ReplyIntent | null;
  reply_content: string | null;
  reply_classified_by: string | null;

  created_at: string;
  updated_at: string;
}

/** Libellés FR pour affichage UI. */
export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  raw: "Brut",
  verified: "Email vérifié",
  qualified: "Qualifié",
  awaiting_review: "En review",
  ready: "Prêt à envoyer",
  edited: "Édité",
  rejected: "Rejeté",
  sent: "Envoyé",
  replied: "Répondu",
  bounced: "Bounce",
  unsubscribed: "Désabonné",
};

/**
 * Couleurs Aurora par statut (badges UI light mode).
 * Toutes les classes sont du Tailwind statique — pas de string interpolation
 * pour que le compilateur Tailwind les inclue dans le bundle.
 */
export const PROSPECT_STATUS_COLORS: Record<ProspectStatus, string> = {
  raw: "bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200",
  verified: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  qualified: "bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200",
  awaiting_review:
    "bg-[color-mix(in_srgb,var(--aurora-color-secondary)_10%,transparent)] text-aurora-orange ring-1 ring-[color-mix(in_srgb,var(--aurora-color-secondary)_30%,transparent)]",
  ready: "bg-emerald-50 text-aurora-green ring-1 ring-emerald-200",
  edited: "bg-violet-50 text-violet-700 ring-1 ring-violet-200",
  rejected: "bg-red-50 text-red-700 ring-1 ring-red-200",
  sent: "bg-blue-50 text-aurora-blue ring-1 ring-blue-200",
  replied: "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200",
  bounced: "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
  unsubscribed: "bg-neutral-100 text-neutral-500 ring-1 ring-neutral-200",
};

/**
 * Renvoie l'URL d'un favicon Google pour un domaine donné (utile pour décorer
 * les tuiles prospect avec le logo de leur entreprise sans héberger d'images).
 *
 * @param website URL complète du site entreprise (ex. "https://example.com")
 * @param size taille en pixels (16, 32, 64, 128, 256)
 */
export function companyFaviconUrl(website: string | null | undefined, size = 64): string | null {
  if (!website) return null;
  try {
    const url = new URL(website.startsWith("http") ? website : `https://${website}`);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=${size}`;
  } catch {
    return null;
  }
}

/**
 * Génère les initiales d'un prospect (1-2 lettres majuscules) pour fallback
 * quand pas de favicon disponible.
 */
export function prospectInitials(
  p: Pick<Prospect, "first_name" | "last_name" | "company_name" | "email">
): string {
  const f = (p.first_name ?? "").trim();
  const l = (p.last_name ?? "").trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  if (p.company_name) return p.company_name.trim().slice(0, 2).toUpperCase();
  return p.email.slice(0, 2).toUpperCase();
}

/**
 * Lit les variantes d'email d'un prospect de façon défensive.
 * Le jsonb réel est `{ variants: [{ subject, body, angles_emphasis }] }`
 * (cf. node `Parse personalization` du workflow Qualified to Ready).
 */
export function getVariants(
  p: Pick<Prospect, "personalization_variants">
): PersonalizationVariant[] {
  const v = p.personalization_variants;
  if (!v || !Array.isArray((v as PersonalizationVariants).variants)) return [];
  return (v as PersonalizationVariants).variants
    .filter(
      (x): x is PersonalizationVariant =>
        !!x && typeof x.subject === "string" && typeof x.body === "string"
    )
    .map((x) => ({
      ...x,
      angles_emphasis: Array.isArray(x.angles_emphasis) ? x.angles_emphasis : [],
    }));
}
